import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { BackContinue } from '@components/back_continue/back_continue'
import { useBookingContext } from '@providers/booking/booking_context'
import { TCartRequestDates } from '@providers/booking/types'
import { MAX_WIDGET_SIZE, STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { i18WithParams as t } from '@shared/locale'
import { maximumSelectableDate } from '@shared/service_types'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { DatePicker } from './date_picker'
import { DatesViewer } from './dates_viewer'
import { Services } from 'service-api'
import { useParams } from 'react-router-dom'
import { getTotalDuration } from './helper'
import { addDays, format } from 'date-fns'
import { DatePickerSkeleton } from './date_picker_skeleton'

export const MIN_REQUEST_DATES = 1
export const MAX_REQUEST_DATES = 5

export const RequestDates = () => {
  const { accountName: locationName = '' } = useParams()
  const [providerDates, setProviderDates] = useState<string[] | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const bookingCtx = useBookingContext()
  const showProviderAvailabilityDates = bookingCtx.state.data?.serviceType?.showProviderAvailabilityDates ?? false
  const overridePrimaryResourceId = bookingCtx.state.cart.overridePrimaryResourceId
  const overrideResourceGroupId = bookingCtx.state.cart.overrideResourceGroupId
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const shouldShowProviderDates =
    (overridePrimaryResourceId || overrideResourceGroupId) && showProviderAvailabilityDates

  // redirect to home if the state has invalid data
  useRedirectNoData()

  useEffect(() => {
    if (bookingCtx.state.currentState !== 'requestDates') {
      bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'requestDates' } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.currentState])

  const getAvailabilityDates = useCallback(async () => {
    if (overridePrimaryResourceId || overrideResourceGroupId) {
      const resourceId = overridePrimaryResourceId || overrideResourceGroupId
      const totalDuration = getTotalDuration(bookingCtx.state.cart)
      const results = await Services.ClientResourceService.getResourceAvailability(locationName, {
        resourceId,
        totalDuration,
        startDate: format(bookingCtx.state.startDate!, 'yyyy-MM-dd'),
        endDate: format(addDays(bookingCtx.state.startDate!, 60), 'yyyy-MM-dd'),
      })

      if (results.length) {
        const dates = results.filter((item) => item.availabilities!.length > 0).map((item) => item.date!)
        setProviderDates(dates)
      }
    }
    setIsLoading(false)
  }, [
    bookingCtx.state.cart,
    bookingCtx.state.startDate,
    locationName,
    overridePrimaryResourceId,
    overrideResourceGroupId,
  ])

  useEffect(() => {
    if (shouldShowProviderDates) {
      setIsLoading(true)
      getAvailabilityDates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowProviderDates])

  const canContinue = useMemo(() => {
    const requests = bookingCtx.state.cart.requestDates || ([] as TCartRequestDates[])
    if (requests.length < MIN_REQUEST_DATES || requests.length > MAX_REQUEST_DATES) {
      return false
    }
    return true
  }, [bookingCtx.state.cart.requestDates])

  const startDate = bookingCtx.state.startDate!
  const maxDate = maximumSelectableDate(startDate, bookingCtx.state)

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Typography variant="h1" sx={{ px: 2 }}>
        {t('requestDates.grooms.title')}
      </Typography>
      <Spacer height={3} />

      <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Typography variant="h5">{t('requestDates.grooms.subTitle')}</Typography>
        <Typography variant="caption">{t('requestDates.grooms.byline')}</Typography>

        <Spacer height={2} />

        <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={3}>
          {shouldShowProviderDates && isLoading ? (
            <DatePickerSkeleton />
          ) : (
            <DatePicker startDate={startDate} maxDate={maxDate} providerDates={providerDates} />
          )}
          <DatesViewer />
        </Stack>
      </Paper>

      <Spacer height={STICKY_FOOTER_HEIGHT} />
      <BackContinue continueEnabled={canContinue} />
    </Box>
  )
}
